import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Download, Search, RefreshCw, Save } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, API, aud } from "@/lib/api";

const STATUSES = [
  "New",
  "Contact attempted",
  "Contacted",
  "Appraisal booked",
  "Future opportunity",
  "Not proceeding",
  "Listed",
  "Sold",
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, avg_value: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [timing, setTiming] = useState("All");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [config, setConfig] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem("bonza_token");
    navigate("/admin/login");
  }, [navigate]);

  const loadLeads = useCallback(async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (status !== "All") params.status = status;
      if (timing !== "All") params.timing = timing;
      const [leadRes, statRes] = await Promise.all([
        api.get("/admin/leads", { params }),
        api.get("/admin/stats"),
      ]);
      setLeads(leadRes.data);
      setStats(statRes.data);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  }, [q, status, timing, logout]);

  useEffect(() => {
    (async () => {
      try {
        await api.get("/auth/me");
        setReady(true);
      } catch {
        logout();
      }
    })();
  }, [logout]);

  useEffect(() => {
    if (ready) loadLeads();
  }, [ready, loadLeads]);

  const openLead = async (id) => {
    try {
      const { data } = await api.get(`/admin/leads/${id}`);
      setSelected(data);
      setNote("");
    } catch {
      toast.error("Could not load lead");
    }
  };

  const changeStatus = async (newStatus) => {
    const { data } = await api.patch(`/admin/leads/${selected.id}`, { status: newStatus });
    setSelected(data);
    loadLeads();
    toast.success("Status updated");
  };

  const addNote = async () => {
    if (!note.trim()) return;
    const { data } = await api.patch(`/admin/leads/${selected.id}`, { note });
    setSelected(data);
    setNote("");
    toast.success("Note added");
  };

  const resend = async () => {
    await api.post(`/admin/leads/${selected.id}/resend`);
    toast.success("Report re-queued for sending");
  };

  const exportCsv = () => {
    const token = localStorage.getItem("bonza_token");
    fetch(`${API}/admin/leads-export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "bonza_leads.csv";
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  const loadConfig = async () => {
    const { data } = await api.get("/admin/config");
    setConfig(data);
  };

  const saveConfig = async () => {
    await api.put("/admin/config", {
      bands: config.bands,
      margin_benchmarks: config.margin_benchmarks,
      occupancy_benchmarks: config.occupancy_benchmarks,
      fitout_depreciation_rate: Number(config.fitout_depreciation_rate),
    });
    toast.success("Assumptions saved");
  };

  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-background text-muted-foreground eyebrow">
        Loading console&hellip;
      </div>
    );
  }

  const inputCls =
    "bg-transparent border border-border rounded-none px-3 py-2 outline-none focus:border-brand transition-colors";

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex flex-col leading-none">
            <span className="font-serif text-2xl tracking-tight">Salons For Sale</span>
            <span className="eyebrow text-[0.55rem] text-muted-foreground mt-0.5">Lead Console</span>
          </Link>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 eyebrow text-muted-foreground hover:text-brand transition-colors"
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-10">
        <Tabs defaultValue="leads" onValueChange={(v) => v === "config" && !config && loadConfig()}>
          <TabsList className="mb-8 bg-muted">
            <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="config" data-testid="tab-config">Assumptions</TabsTrigger>
          </TabsList>

          {/* LEADS */}
          <TabsContent value="leads">
            <div className="grid grid-cols-2 md:grid-cols-3 border-t border-l border-border mb-8 max-w-2xl">
              <div className="border-b border-r border-border p-5">
                <div className="eyebrow text-muted-foreground mb-1">Total leads</div>
                <div className="font-serif text-4xl">{stats.total}</div>
              </div>
              <div className="border-b border-r border-border p-5">
                <div className="eyebrow text-muted-foreground mb-1">New</div>
                <div className="font-serif text-4xl text-brand">{stats.new}</div>
              </div>
              <div className="border-b border-r border-border p-5 col-span-2 md:col-span-1">
                <div className="eyebrow text-muted-foreground mb-1">Avg value</div>
                <div className="font-serif text-4xl">{aud(stats.avg_value)}</div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="flex items-center gap-2 border border-border px-3 py-2 flex-1 min-w-[200px]">
                <Search size={16} className="text-muted-foreground" />
                <input
                  data-testid="lead-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && loadLeads()}
                  placeholder="Search name, email, mobile, state"
                  className="bg-transparent outline-none w-full text-sm"
                />
              </div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="filter-status" className="w-[180px] rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All statuses</SelectItem>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                data-testid="apply-filters"
                onClick={loadLeads}
                className="inline-flex items-center gap-2 border border-border px-4 py-2 eyebrow hover:border-brand transition-colors"
              >
                <RefreshCw size={14} /> Apply
              </button>
              <button
                data-testid="export-csv"
                onClick={exportCsv}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 eyebrow hover:bg-brand transition-colors"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>

            <div className="border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="border-b border-border text-left eyebrow text-muted-foreground">
                    <th className="p-4 font-semibold">Date</th>
                    <th className="p-4 font-semibold">Name</th>
                    <th className="p-4 font-semibold">Salon</th>
                    <th className="p-4 font-semibold">State</th>
                    <th className="p-4 font-semibold text-center">Score</th>
                    <th className="p-4 font-semibold text-right">SDE</th>
                    <th className="p-4 font-semibold text-right">Value</th>
                    <th className="p-4 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && (
                    <tr>
                      <td colSpan={8} className="p-10 text-center text-muted-foreground">
                        No leads yet.
                      </td>
                    </tr>
                  )}
                  {leads.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`lead-row-${l.id}`}
                      onClick={() => openLead(l.id)}
                      className="border-b border-border last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
                    >
                      <td className="p-4 text-muted-foreground whitespace-nowrap">{fmtDate(l.created_at)}</td>
                      <td className="p-4 font-medium whitespace-nowrap">{l.contact?.first_name} {l.contact?.last_name || ""}</td>
                      <td className="p-4">{l.contact?.business_name || "—"}</td>
                      <td className="p-4">{l.contact?.state}</td>
                      <td className="p-4 text-center font-medium">{l.calc?.saleability_score ?? "—"}</td>
                      <td className="p-4 text-right">{aud(l.calc?.sde)}</td>
                      <td className="p-4 text-right font-medium">{aud(l.calc?.recommended_value)}</td>
                      <td className="p-4">
                        <span className="inline-block px-2.5 py-1 border border-border eyebrow text-[0.6rem]">
                          {l.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* CONFIG */}
          <TabsContent value="config">
            {!config ? (
              <div className="text-muted-foreground eyebrow">Loading assumptions&hellip;</div>
            ) : (
              <div className="max-w-3xl" data-testid="config-editor">
                <h3 className="font-serif text-3xl tracking-tight mb-6">SDE multiple bands</h3>
                <div className="space-y-3 mb-10">
                  {config.bands.map((b, i) => (
                    <div key={i} className="grid grid-cols-4 gap-3 items-center">
                      <input
                        className={inputCls}
                        value={b.label}
                        onChange={(e) => {
                          const bands = [...config.bands];
                          bands[i] = { ...b, label: e.target.value };
                          setConfig({ ...config, bands });
                        }}
                      />
                      <input
                        className={inputCls}
                        type="number"
                        placeholder="Max SDE"
                        value={b.max_sde ?? ""}
                        onChange={(e) => {
                          const bands = [...config.bands];
                          bands[i] = { ...b, max_sde: e.target.value === "" ? null : Number(e.target.value) };
                          setConfig({ ...config, bands });
                        }}
                      />
                      <input
                        className={inputCls}
                        type="number"
                        step="0.05"
                        value={b.low}
                        onChange={(e) => {
                          const bands = [...config.bands];
                          bands[i] = { ...b, low: Number(e.target.value) };
                          setConfig({ ...config, bands });
                        }}
                      />
                      <input
                        className={inputCls}
                        type="number"
                        step="0.05"
                        value={b.high}
                        onChange={(e) => {
                          const bands = [...config.bands];
                          bands[i] = { ...b, high: Number(e.target.value) };
                          setConfig({ ...config, bands });
                        }}
                      />
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">Columns: label · max SDE (blank = no cap) · low multiple · high multiple</p>
                </div>

                <h3 className="font-serif text-3xl tracking-tight mb-6">Benchmarks</h3>
                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                  {["strong", "moderate", "low"].map((k) => (
                    <div key={k}>
                      <label className="eyebrow text-muted-foreground">SDE margin {k}</label>
                      <input
                        className={`${inputCls} w-full mt-1`}
                        type="number"
                        step="0.01"
                        value={config.margin_benchmarks[k]}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            margin_benchmarks: { ...config.margin_benchmarks, [k]: Number(e.target.value) },
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
                <div className="grid sm:grid-cols-3 gap-4 mb-10">
                  {["healthy", "watch"].map((k) => (
                    <div key={k}>
                      <label className="eyebrow text-muted-foreground">Occupancy {k}</label>
                      <input
                        className={`${inputCls} w-full mt-1`}
                        type="number"
                        step="0.01"
                        value={config.occupancy_benchmarks[k]}
                        onChange={(e) =>
                          setConfig({
                            ...config,
                            occupancy_benchmarks: { ...config.occupancy_benchmarks, [k]: Number(e.target.value) },
                          })
                        }
                      />
                    </div>
                  ))}
                  <div>
                    <label className="eyebrow text-muted-foreground">Fit-out dep. rate</label>
                    <input
                      className={`${inputCls} w-full mt-1`}
                      type="number"
                      step="0.05"
                      value={config.fitout_depreciation_rate}
                      onChange={(e) =>
                        setConfig({ ...config, fitout_depreciation_rate: e.target.value })
                      }
                    />
                  </div>
                </div>

                <button
                  data-testid="save-config"
                  onClick={saveConfig}
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 eyebrow hover:bg-brand transition-colors"
                >
                  <Save size={15} /> Save assumptions
                </button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Lead detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="lead-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-3xl font-light tracking-tight">
                  {selected.contact?.first_name} · {aud(selected.calc?.recommended_value)}
                </DialogTitle>
              </DialogHeader>

              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div className="flex items-center gap-3">
                  <Select value={selected.status} onValueChange={changeStatus}>
                    <SelectTrigger data-testid="detail-status" className="rounded-none">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <button
                  data-testid="detail-resend"
                  onClick={resend}
                  className="inline-flex items-center justify-center gap-2 border border-border px-4 py-2 eyebrow hover:border-brand transition-colors"
                >
                  <RefreshCw size={14} /> Resend report
                </button>
              </div>

              <Section title="Contact">
                <Row k="Email" v={selected.contact?.email} />
                <Row k="Mobile" v={selected.contact?.mobile} />
                <Row k="State" v={selected.contact?.state} />
                <Row k="Suburb" v={selected.contact?.suburb || "—"} />
                <Row k="Sale timing" v={selected.contact?.sale_timing || "—"} />
              </Section>

              <Section title="Calculations">
                <Row k="SDE" v={aud(selected.calc?.sde)} />
                <Row k="SDE margin" v={`${(selected.calc?.sde_margin * 100).toFixed(1)}% (${selected.calc?.sde_margin_category})`} />
                <Row k="Range" v={`${aud(selected.calc?.valuation_low)} – ${aud(selected.calc?.valuation_high)}`} />
                <Row k="Central" v={aud(selected.calc?.valuation_central)} />
                <Row k="Multiple" v={`${selected.calc?.multiple_low}x – ${selected.calc?.multiple_high}x`} />
                <Row k="Asset-based" v={aud(selected.calc?.asset_based_value)} />
                <Row k="Station use" v={selected.calc?.station_utilisation_text} />
                <Row k="Occupancy" v={`${(selected.calc?.occupancy_cost_pct * 100).toFixed(1)}%`} />
              </Section>

              {selected.intel && (
                <Section title="AI Sales Intelligence">
                  <div className="bg-muted border-l-2 border-brand p-3 text-sm leading-relaxed mb-3" data-testid="detail-ai-summary">
                    {selected.intel.salesperson_summary}
                  </div>
                  <Row k="Saleability score" v={`${selected.calc?.saleability_score ?? "—"} / 100`} />
                  <Row k="Lead quality" v={selected.intel.lead_quality} />
                  <Row k="Lead temperature" v={selected.intel.lead_temperature} />
                  <Row k="Motivation" v={selected.intel.motivation} />
                  <Row k="Sale readiness" v={selected.intel.estimated_sale_readiness} />
                  <Row k="Broker priority" v={selected.intel.broker_priority} />
                  <Row k="Growth opportunity" v={selected.intel.growth_opportunity} />
                  <Row k="Recommended follow-up" v={selected.intel.recommended_follow_up} />
                  {Array.isArray(selected.intel.talking_points) && (
                    <div className="mt-3">
                      <div className="eyebrow text-muted-foreground mb-2">Talking points</div>
                      <ul className="list-disc pl-5 space-y-1 text-sm">
                        {selected.intel.talking_points.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Section>
              )}

              {selected.calc?.warnings?.length > 0 && (
                <Section title="Warnings">
                  {selected.calc.warnings.map((w, i) => (
                    <p key={i} className="text-sm text-brand py-1">{w}</p>
                  ))}
                </Section>
              )}

              <Section title="Business answers">
                {Object.entries(selected.answers || {}).map(([k, v]) => (
                  <Row key={k} k={k.replace(/_/g, " ")} v={String(v)} />
                ))}
              </Section>

              <Section title="Notes">
                {(selected.notes || []).map((n, i) => (
                  <div key={i} className="border-l-2 border-border pl-3 py-1 text-sm">
                    <span className="text-muted-foreground">{fmtDate(n.at)}: </span>
                    {n.text}
                  </div>
                ))}
                <div className="flex gap-2 mt-3">
                  <input
                    data-testid="note-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add an internal note"
                    className="flex-1 border border-border px-3 py-2 text-sm outline-none focus:border-brand"
                  />
                  <button
                    data-testid="add-note"
                    onClick={addNote}
                    className="bg-primary text-primary-foreground px-4 eyebrow hover:bg-brand transition-colors"
                  >
                    Add
                  </button>
                </div>
              </Section>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mt-6">
    <div className="eyebrow text-brand mb-3">{title}</div>
    <div className="border-t border-border">{children}</div>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-border text-sm">
    <span className="text-muted-foreground capitalize">{k}</span>
    <span className="font-medium text-right">{v}</span>
  </div>
);
